import type { DocumentLabels, PrintLayoutKind, RawPrintSettings } from "../types";
import { escapeHtml } from "./escapeHtml";
import { buildFontFaceStyle } from "./fonts";
import { resolvePrintLayout } from "./printLayout";
import { buildDocumentCss } from "./documentCss";
import { buildHeaderHtml } from "./buildHeader";
import { buildFooterHtml } from "./buildFooter";
import type { HeaderTokenValues } from "./headerTokens";

export { SEAL_RESERVE_BOTTOM_MM, PAGE_SIZES_MM } from "./documentCss";
export type PrintZone = "page" | "header" | "body" | "sign" | "footer";

export interface BuildPrintDocumentArgs {
  title?: string | null;
  content: string;
  printSettings?: RawPrintSettings | null;
  kind?: PrintLayoutKind;
  labels: DocumentLabels;
  headerTokens?: HeaderTokenValues;
  /** Base URL for font files; enables @font-face so server-side Chromium can load custom fonts. */
  fontBaseUrl?: string | null;
  /** True only in the signature flow: reserves bottom space for the digital seal. */
  reserveSealSpace?: boolean;
  preview?: {
    pageLabel?: string | null;
    zones?: boolean;
    digitalSeal?: { detail: string; instruction: string; validationUrl: string; qrLabel: string } | null;
  } | null;
}

// Posts the clicked area to the parent window (DocumentPreview). Only in preview mode.
const ZONE_SCRIPT = `<script>
document.addEventListener("click", function (event) {
  var el = event.target.closest("[data-zone]");
  if (!el) return;
  event.preventDefault();
  window.parent.postMessage({ type: "akuna-print-zone", zone: el.getAttribute("data-zone") }, "*");
});
</script>`;

export function buildPrintDocument({
  title, content, printSettings, kind = "term", labels, headerTokens, fontBaseUrl,
  reserveSealSpace = false, preview = null,
}: BuildPrintDocumentArgs): string {
  const layout = resolvePrintLayout(printSettings, kind);
  const fontFace = fontBaseUrl ? buildFontFaceStyle(layout.fontId, fontBaseUrl) : "";
  const counter = preview?.pageLabel ? `<div class="doc-preview-counter">${escapeHtml(preview.pageLabel)}</div>` : "";
  const digitalSeal = preview?.digitalSeal ? buildFakeDigitalSeal(preview.digitalSeal) : "";
  return `<!doctype html>
<html lang="${labels.lang}">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title || "")}</title>
<style>${fontFace}${buildDocumentCss(layout, { reserveSealSpace, preview: Boolean(preview) })}</style>
</head>
<body>
<main class="doc-sheet" data-zone="page">
${buildHeaderHtml(layout, headerTokens ?? {}, labels.headerAlt)}
${content}
<div class="doc-spacer"></div>
${buildFooterHtml(layout, labels.footerAlt)}
${digitalSeal}
${counter}
</main>
${preview?.zones ? ZONE_SCRIPT : ""}
</body>
</html>`;
}

function buildFakeDigitalSeal(seal: { detail: string; instruction: string; validationUrl: string; qrLabel: string }): string {
  const size = 21;
  const modules: string[] = [];
  const inFinder = (x: number, y: number, left: number, top: number) => x >= left && x < left + 7 && y >= top && y < top + 7;
  const finderOn = (x: number, y: number, left: number, top: number) => {
    const dx = x - left;
    const dy = y - top;
    return dx === 0 || dx === 6 || dy === 0 || dy === 6 || (dx >= 2 && dx <= 4 && dy >= 2 && dy <= 4);
  };
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const finders = [[0, 0], [14, 0], [0, 14]];
      const finder = finders.find(([left, top]) => inFinder(x, y, left, top));
      const on = finder
        ? finderOn(x, y, finder[0], finder[1])
        : (x * 11 + y * 7 + x * y) % 5 < 2;
      if (on) modules.push(`<rect x="${x}" y="${y}" width="1" height="1" />`);
    }
  }
  const svg = `<svg viewBox="-1 -1 23 23" role="img" aria-label="${escapeHtml(seal.qrLabel)}" xmlns="http://www.w3.org/2000/svg"><rect x="-1" y="-1" width="23" height="23" fill="#fff"/>${modules.join("")}</svg>`;
  return `<aside class="doc-digital-seal" data-zone="sign"><div class="doc-digital-seal__row"><div class="doc-digital-seal__text"><p class="doc-digital-seal__detail">${escapeHtml(seal.detail)}</p><p class="doc-digital-seal__instruction">${escapeHtml(seal.instruction)} ${escapeHtml(seal.validationUrl)}</p></div><div class="doc-digital-seal__qr">${svg}</div></div></aside>`;
}
